import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { workersApi } from '@/services/workers'
import type { Worker, WorkerAttendance } from '@/types'

export type PunchStatus = 'complete' | 'working' | 'missing' | 'absent'

export type AttendancePunch = WorkerAttendance & {
  worker?: string | number | null
}

export function todayIsoDate() {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

export function addIsoDays(isoDate: string, days: number) {
  const [year, month, day] = isoDate.split('-').map(Number)
  const date = new Date(year, (month || 1) - 1, day || 1)
  date.setDate(date.getDate() + days)
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0')
  const nextDay = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${nextMonth}-${nextDay}`
}

export function workerName(worker: Pick<Worker, 'display_name' | 'full_name' | 'worker_name' | 'first_name' | 'last_name' | 'id'> | null | undefined) {
  if (!worker) return 'Worker'
  const combined = [worker.first_name, worker.last_name].filter(Boolean).join(' ').trim()
  return worker.display_name || worker.full_name || worker.worker_name || combined || `Worker ${worker.id}`
}

export function workerCode(worker: Worker | null | undefined) {
  return String(worker?.worker_code || worker?.user_registration_number || worker?.id || '').trim()
}

export function workerDepartment(worker: Worker | null | undefined) {
  return worker?.role_name || worker?.linked_post_role_name || worker?.worker_skill_mappings?.find((row) => row.is_primary)?.skill_role_title || '—'
}

export function workerTitle(worker: Worker | null | undefined) {
  const title = worker?.worker_skill_mappings?.find((row) => row.is_primary)?.skill_role_title || worker?.linked_post_role_name || ''
  const department = workerDepartment(worker)
  return title && title !== department ? title : ''
}

export function punchWorkerId(punch: AttendancePunch) {
  return String(punch.worker_id || punch.worker || '')
}

export function punchDate(punch: AttendancePunch) {
  const raw = punch.check_in_local || punch.check_in || punch.timestamp
  if (!raw) return ''
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return String(raw).slice(0, 10)
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  return `${parsed.getFullYear()}-${month}-${day}`
}

export function formatClock(value: string | null | undefined) {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export function formatDay(value: string | null | undefined) {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })
}

export function workedLabel(punch: Pick<AttendancePunch, 'total_minutes' | 'check_in' | 'check_in_local' | 'check_out' | 'check_out_local'>) {
  let minutes: number | null = null
  if (punch.total_minutes != null && Number.isFinite(Number(punch.total_minutes))) {
    minutes = Number(punch.total_minutes)
  } else {
    const start = new Date(punch.check_in_local || punch.check_in || '').getTime()
    if (!Number.isFinite(start)) return '—'
    const endRaw = punch.check_out_local || punch.check_out
    const end = endRaw ? new Date(endRaw).getTime() : Date.now()
    if (!Number.isFinite(end)) return '—'
    minutes = Math.max(Math.round((end - start) / 60000), 0)
  }
  const wholeMinutes = Math.max(Math.round(minutes), 0)
  const hours = Math.floor(wholeMinutes / 60)
  const remainingMinutes = wholeMinutes % 60
  if (hours <= 0) return `${remainingMinutes}m`
  if (remainingMinutes === 0) return `${hours}h`
  return `${hours}h ${remainingMinutes}m`
}

export function punchStatus(punch: AttendancePunch, today = todayIsoDate()): PunchStatus {
  const day = punchDate(punch)
  if (!punch.check_in) return 'missing'
  if (!punch.check_out) return day && day < today ? 'missing' : 'working'
  return 'complete'
}

export function methodLabel(source: string | null | undefined) {
  if (source === 'adms') return 'Device'
  if (source === 'mobile') return 'Mobile'
  if (source === 'portal') return 'Portal'
  return source || '—'
}

export function useAttendanceDirectory() {
  const workersQuery = useQuery({
    queryKey: ['attendance-directory-workers'],
    queryFn: () => workersApi.getWorkers({ page_size: 200, active: true }),
  })
  const workers = workersQuery.data?.results ?? []
  const byId = new Map(workers.map((worker) => [String(worker.worker_id ?? worker.id), worker]))
  workers.forEach((worker) => byId.set(String(worker.id), worker))
  return { workers, byId, isLoading: workersQuery.isLoading, error: workersQuery.error }
}

export function useAttendancePunches(startDate: string, endDate = startDate) {
  return useQuery({
    queryKey: ['attendance-punches', startDate, endDate],
    queryFn: () => workersApi.getWorkerAttendanceList({
      start_date: startDate,
      end_date: endDate,
      page_size: 500,
      ordering: '-timestamp',
    }),
  })
}

export type AdminAttendancePayload = {
  workerId: string
  status: boolean
  timestamp?: string
  latitude?: number
  longitude?: number
}

export function useAttendancePunchActions() {
  const queryClient = useQueryClient()
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['attendance-directory-workers'] })
    await queryClient.invalidateQueries({ queryKey: ['attendance-punches'] })
    await queryClient.invalidateQueries({ queryKey: ['worker-attendance'] })
    await queryClient.invalidateQueries({ queryKey: ['worker-attendance-latest'] })
  }
  const record = useMutation({
    mutationFn: (payload: AdminAttendancePayload) => workersApi.createWorkerAttendance({
      worker: payload.workerId,
      status: payload.status,
      source: 'portal',
      ...(payload.timestamp ? { timestamp: payload.timestamp } : {}),
      ...(payload.latitude !== undefined ? { latitude: payload.latitude } : {}),
      ...(payload.longitude !== undefined ? { longitude: payload.longitude } : {}),
    }),
    onSuccess: refresh,
  })
  return { record }
}
